/* v8 ignore start */
import { ref, reactive, computed, unref } from "vue";
import get from "lodash/get";

export default function useFormSequence(formConfig, context) {
  const currentStepId = ref(context.startStep ?? formConfig[0].id);
  const history = ref([]);
  const answers = reactive(new Map());
  const loading = ref(false);
  const nextResult = ref(null);

  // Tracking submission for clearing the form on success
  // after the modal is closed.
  const formSubmitted = ref(false);

  const resolvedStep = computed(() => {
    const step = getStepById(currentStepId.value);
    return {
      ...step,
      props:
        typeof step.props === "function" ? step.props(answers) : step.props,
    };
  });

  const resetState = () => {
    currentStepId.value = context.startStep ?? formConfig[0].id;
    history.value = [];
    answers.clear();
    nextResult.value = null;
    formSubmitted.value = false;
  };

  const getStepById = (stepId) => {
    return formConfig.find((q) => q.id === stepId);
  };

  const goToStep = (stepId) => {
    const step = getStepById(stepId);
    if (step) {
      history.value.push(currentStepId.value);
      if (step.final) {
        cleanupStepAnswerPath();
      }
      currentStepId.value = stepId;
    }
  };

  const goBack = () => {
    if (history.value.length > 0) {
      currentStepId.value = history.value.pop();
    }
  };

  /*
   * If you go back and change an answer, we need to remove all answers that you didn't reach
   */
  const cleanupStepAnswerPath = () => {
    answers.forEach((value, key) => {
      if (!history.value.find((historyStep) => historyStep === key)) {
        const stepConfig = getStepById(key);
        if (get(stepConfig, "skipAnswerCleanup", false)) {
          // do nothing. This is useful for if you have a step that populates answers
          // but could be skipped in the flow (see useCreateActivitySequence.js)
        } else {
          answers.delete(key);
        }
      }
    });
  };

  const submitAnswer = async (answer) => {
    loading.value = true;

    try {
      const previousAnswer = answers.has(currentStepId.value)
        ? answers.get(currentStepId.value)
        : null;
      const step = getStepById(currentStepId.value);
      const result = await step.next(answer, answers, context, previousAnswer);

      if (step.final) {
        nextResult.value = {
          success: result.success,
          message:
            result.message ||
            (result.success ? "Submitted" : "Submission failed"),
        };

        if (result.success) {
          formSubmitted.value = true;
          return result.success;
        }
      } else {
        // We don't set an answer or move to the next step on final steps,
        // this prevents "end" being added to the answers map and causing reactivity issues.
        answers.set(currentStepId.value, unref(answer));

        if (result?.nextStepId) {
          goToStep(result.nextStepId);
          return true;
        }
      }
    } catch (error) {
      console.error(error);
      nextResult.value = {
        success: false,
        message: error.message || "An unexpected error occurred.",
      };
      return true;
    } finally {
      loading.value = false;
    }
  };

  return {
    resolvedStep,
    answers,
    history,
    loading,
    nextResult,
    formSubmitted,
    resetState,
    submitAnswer,
    goBack,
  };
}
/* v8 ignore stop */
